import getInitials from 'initials';
import { FC, useMemo } from 'react';

import { Icon, IconProps } from './Icon';
import { Stack } from './Stack';
import { Caption, Text } from './Typography';
import { cn } from './utils/cn';

type TenantCardProps = {
  icon?: {
    name: IconProps['name'];
  };
  showBadge?: boolean;
  tenantLogoUrl?: string;
  tenantName: string;
  userDisplayName: string;
};

export const TenantCard: FC<TenantCardProps> = ({
  icon,
  showBadge,
  tenantLogoUrl,
  tenantName,
  userDisplayName,
}) => {
  const initials = useMemo(
    () => getInitials(tenantName).slice(0, 3),
    [tenantName],
  );

  return (
    <Stack align="center" className="shrink-0" gap={2} direction="horizontal">
      <Stack
        align="center"
        className={cn(
          'relative size-8 shrink-0 overflow-hidden rounded-md',
          tenantLogoUrl
            ? 'border border-border-primary'
            : 'bg-bg-strong-secondary text-text-strong',
        )}
        justify="center"
        gap={2}
      >
        {tenantLogoUrl ? (
          <img
            alt="logo"
            className="size-8 object-contain"
            src={tenantLogoUrl}
          />
        ) : (
          <Caption size="2xs" color="inherit">
            {initials}
          </Caption>
        )}
        {showBadge && (
          <div className="absolute -top-0.5 -right-0.5 size-3 rounded-tr-lg rounded-bl-sm border-2 border-bg-secondary bg-bg-negative-strong-primary" />
        )}
      </Stack>
      <Stack align="stretch" className="min-w-0">
        <Text className="truncate" weight="semibold" size="sm">
          {tenantName}
        </Text>
        <Text className="truncate" color="tertiary" size="xs">
          {userDisplayName}
        </Text>
      </Stack>
      {icon && (
        <Icon className="shrink-0 text-text-tertiary" name={icon.name} />
      )}
    </Stack>
  );
};
